/**
 * LockService - נעילות קריאות שירות
 * Hybrid (NAS): נעילה ב-REST + עדכונים ב-Socket.IO (בלי heartbeat / polling)
 * Firestore: onSnapshot לזיהוי השתלטות
 */

import {
  doc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  getDoc,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import hybridApiClient from './hybridApiClient';
import { hybridFlags } from './hybridFlags';
import { appRealtimeService } from './AppRealtimeService';

export interface RepairLock {
  userId: string;
  userName: string;
  userEmail: string;
  lockedAt: Timestamp;
  lastHeartbeat: Timestamp;
  isActive: boolean;
}

/** תשובת API / socket — המרה ל־RepairLock לתצוגה */
export function repairLockFromApiPayload(raw: unknown): RepairLock | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (!o.isActive) return null;
  const hbRaw = o.lastHeartbeat ?? o.lockedAt;
  if (hbRaw == null) return null;
  const hb =
    typeof hbRaw === 'number'
      ? hbRaw
      : new Date(String(hbRaw)).getTime();
  if (!Number.isFinite(hb)) return null;
  const laRaw = o.lockedAt != null ? o.lockedAt : hbRaw;
  const la =
    typeof laRaw === 'number' ? laRaw : new Date(String(laRaw)).getTime();
  const laMs = Number.isFinite(la) ? la : hb;
  return {
    userId: String(o.userId || ''),
    userName: String(o.userName || 'משתמש'),
    userEmail: String(o.userEmail || ''),
    lockedAt: Timestamp.fromMillis(laMs),
    lastHeartbeat: Timestamp.fromMillis(hb),
    isActive: true,
  };
}

class LockService {
  private lockListeners: Map<string, () => void> = new Map();
  private readonly LOCK_TIMEOUT = 120000;

  private useHybridRepairs(): boolean {
    return hybridFlags.useQnapRepairs || hybridFlags.disableFirestoreReads;
  }

  private async ensureSocket(userId: string): Promise<boolean> {
    await appRealtimeService.connect(userId);
    return appRealtimeService.isConnected();
  }

  async tryLockRepair(
    repairId: string,
    userId: string,
    userName: string,
    userEmail: string,
  ): Promise<{ success: boolean; lockedBy?: RepairLock }> {
    if (this.useHybridRepairs()) {
      try {
        const res = await hybridApiClient.post(
          `/api/repairs/${encodeURIComponent(repairId)}/lock/try`,
          { userName, userEmail },
        );
        const data = res.data as { success?: boolean; lockedBy?: unknown };
        if (data.success === false && data.lockedBy) {
          const lockedBy = repairLockFromApiPayload(data.lockedBy);
          if (lockedBy) return { success: false, lockedBy };
          return { success: false };
        }
        if (await this.ensureSocket(userId)) {
          appRealtimeService.holdRepairEditLock(repairId);
        }
        return { success: true };
      } catch (error) {
        console.error('Error locking repair (API):', error);
        return { success: false };
      }
    }
    try {
      const repairRef = doc(db, 'repairs', repairId);
      const repairDoc = await getDoc(repairRef);

      if (!repairDoc.exists()) {
        console.error('Repair not found:', repairId);
        return { success: false };
      }

      const currentLock = repairDoc.data()?.editingBy as RepairLock | undefined;

      if (currentLock && currentLock.isActive && currentLock.lastHeartbeat) {
        if (currentLock.userId === userId) {
          await updateDoc(repairRef, {
            'editingBy.userName': userName,
            'editingBy.userEmail': userEmail,
            'editingBy.lastHeartbeat': serverTimestamp(),
            'editingBy.isActive': true,
            updatedAt: serverTimestamp(),
          });
          this.listenToLockChanges(repairId, userId);
          return { success: true };
        }

        const lockAge = Date.now() - currentLock.lastHeartbeat.toMillis();
        if (lockAge < this.LOCK_TIMEOUT) {
          return { success: false, lockedBy: currentLock };
        }
      }

      const lockData: RepairLock = {
        userId,
        userName,
        userEmail,
        lockedAt: Timestamp.now(),
        lastHeartbeat: Timestamp.now(),
        isActive: true,
      };

      await updateDoc(repairRef, {
        editingBy: lockData,
        updatedAt: serverTimestamp(),
      });

      this.listenToLockChanges(repairId, userId);
      return { success: true };
    } catch (error) {
      console.error('Error locking repair:', error);
      return { success: false };
    }
  }

  async unlockRepair(repairId: string, userId: string): Promise<void> {
    this.stopListening(repairId);
    if (this.useHybridRepairs()) {
      try {
        await hybridApiClient.post(`/api/repairs/${encodeURIComponent(repairId)}/lock/release`);
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      const repairRef = doc(db, 'repairs', repairId);
      const repairDoc = await getDoc(repairRef);

      if (!repairDoc.exists()) return;

      const currentLock = repairDoc.data()?.editingBy as RepairLock | undefined;
      if (currentLock && currentLock.userId === userId) {
        await updateDoc(repairRef, {
          'editingBy.isActive': false,
          updatedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error('Error unlocking repair:', error);
    }
  }

  async takeoverLock(
    repairId: string,
    userId: string,
    userName: string,
    userEmail: string,
  ): Promise<boolean> {
    if (this.useHybridRepairs()) {
      try {
        const res = await hybridApiClient.post(
          `/api/repairs/${encodeURIComponent(repairId)}/lock/takeover`,
          { userName, userEmail },
        );
        const ok = Boolean((res.data as { success?: boolean })?.success);
        if (ok && (await this.ensureSocket(userId))) {
          appRealtimeService.holdRepairEditLock(repairId);
        }
        return ok;
      } catch (error) {
        console.error('Error taking over lock (API):', error);
        return false;
      }
    }
    try {
      const repairRef = doc(db, 'repairs', repairId);
      const lockData: RepairLock = {
        userId,
        userName,
        userEmail,
        lockedAt: Timestamp.now(),
        lastHeartbeat: Timestamp.now(),
        isActive: true,
      };

      await updateDoc(repairRef, {
        editingBy: lockData,
        updatedAt: serverTimestamp(),
      });

      this.listenToLockChanges(repairId, userId);
      return true;
    } catch (error) {
      console.error('Error taking over lock:', error);
      return false;
    }
  }

  /**
   * האזנה לשינויי נעילה — Socket.IO (hybrid) או Firestore snapshot
   */
  subscribeToLockUpdates(
    repairId: string,
    userId: string,
    onUpdate: (lock: RepairLock | null) => void,
  ): () => void {
    this.stopListening(repairId);

    if (this.useHybridRepairs()) {
      let active = true;
      let unsubSocket = () => {};
      void this.ensureSocket(userId).then((ok) => {
        if (!active || !ok) return;
        unsubSocket = appRealtimeService.watchRepairEditLock(repairId, (raw) => {
          onUpdate(repairLockFromApiPayload(raw));
        });
      });
      const cleanup = () => {
        active = false;
        unsubSocket();
        this.lockListeners.delete(repairId);
      };
      this.lockListeners.set(repairId, cleanup);
      return cleanup;
    }

    const repairRef = doc(db, 'repairs', repairId);
    const unsubscribe = onSnapshot(repairRef, (snapshot) => {
      if (!snapshot.exists()) {
        onUpdate(null);
        return;
      }
      const currentLock = snapshot.data()?.editingBy as RepairLock | undefined;
      if (currentLock && currentLock.isActive) {
        onUpdate(currentLock);
      } else {
        onUpdate(null);
      }
    });
    this.lockListeners.set(repairId, unsubscribe);
    return unsubscribe;
  }

  private listenToLockChanges(repairId: string, userId: string) {
    if (this.useHybridRepairs()) return;
    this.subscribeToLockUpdates(repairId, userId, (lock) => {
      if (lock && lock.userId !== userId && lock.isActive) {
        console.warn('⚠️ Someone else took over the lock:', lock.userName);
      }
    });
  }

  private stopListening(repairId: string) {
    const unsubscribe = this.lockListeners.get(repairId);
    if (unsubscribe) {
      unsubscribe();
      this.lockListeners.delete(repairId);
    }
  }

  async isRepairLocked(
    repairId: string,
    currentUserId: string,
  ): Promise<{ locked: boolean; lockedBy?: RepairLock }> {
    if (this.useHybridRepairs()) {
      try {
        const res = await hybridApiClient.get(`/api/repairs/${encodeURIComponent(repairId)}/lock`);
        const eff = repairLockFromApiPayload((res.data as { editingBy?: unknown })?.editingBy);
        if (!eff) return { locked: false };
        if (eff.userId === currentUserId) return { locked: false };
        return { locked: true, lockedBy: eff };
      } catch (error) {
        console.error('Error checking lock (API):', error);
        return { locked: false };
      }
    }
    try {
      const repairRef = doc(db, 'repairs', repairId);
      const repairDoc = await getDoc(repairRef);

      if (!repairDoc.exists()) return { locked: false };

      const currentLock = repairDoc.data()?.editingBy as RepairLock | undefined;
      if (!currentLock || !currentLock.isActive || !currentLock.lastHeartbeat) {
        return { locked: false };
      }
      if (currentLock.userId === currentUserId) return { locked: false };

      const lockAge = Date.now() - currentLock.lastHeartbeat.toMillis();
      if (lockAge >= this.LOCK_TIMEOUT) return { locked: false };

      return { locked: true, lockedBy: currentLock };
    } catch (error) {
      console.error('Error checking lock status:', error);
      return { locked: false };
    }
  }

  cleanup() {
    this.lockListeners.forEach((unsubscribe) => unsubscribe());
    this.lockListeners.clear();
  }
}

export const lockService = new LockService();
